import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { Helmet } from "react-helmet-async";
import { CheckCircle, Phone, Shield, RefreshCw, Lock, Zap, Clock, AlertTriangle, Image } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const LandscapingWebsiteMaintenance = () => {
  const faqs = [
    {
      question: "What is landscaping website maintenance?",
      answer: "Landscaping website maintenance includes security updates, backups, performance monitoring, and content changes—especially portfolio updates. It keeps your site secure, fast, and showcasing your latest projects."
    },
    {
      question: "How much does landscaping website maintenance cost?",
      answer: "Standalone maintenance typically costs $75–$200/month for portfolio-heavy sites. Our subscription includes maintenance in the $200–$300/month package—no separate fees."
    },
    {
      question: "Why do landscaping websites need more maintenance than other sites?",
      answer: "Landscaping sites have frequent portfolio updates, project galleries, and high-resolution images. This requires more storage management, image optimization, and content updates than simple brochure sites."
    },
    {
      question: "Is maintenance included in your subscription?",
      answer: "Yes. Our $200–$300/month subscription includes hosting, security, backups, performance monitoring, and unlimited reasonable content/portfolio updates."
    },
    {
      question: "How often should I update my landscaping portfolio?",
      answer: "Add new projects as you complete them—ideally monthly during busy season. Fresh portfolio content helps with SEO and shows visitors your current work quality."
    },
    {
      question: "What happens if I don't maintain my landscaping website?",
      answer: "Without maintenance: security risks accumulate, images may slow your site, outdated portfolios hurt credibility, and broken elements lose leads. Regular maintenance prevents all of this."
    },
    {
      question: "Can I add new projects to my portfolio myself?",
      answer: "With DIY builders, yes—but you manage everything. With our subscription, just send us photos and project details. We handle formatting, optimization, and upload."
    },
    {
      question: "What's included in website maintenance?",
      answer: "Security patches, software updates, backups, uptime monitoring, image optimization, SSL renewal, and content updates. For landscaping sites, we also optimize portfolio galleries for speed."
    },
    {
      question: "Do you maintain websites you didn't build?",
      answer: "We focus on subscription clients where we build and maintain. For existing sites, we recommend a full rebuild under our subscription for best results and ongoing support."
    },
    {
      question: "How do portfolio updates work with maintenance?",
      answer: "Send us project photos (before/after preferred) and brief details. We format, optimize images, and add to your portfolio—typically within 24–48 hours."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://www.leafrushmarketing.com/landscaping-website-maintenance/#faq",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.leafrushmarketing.com/#organization",
    "name": "Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com",
    "logo": "https://www.leafrushmarketing.com/og-image.jpg",
    "description": "Website maintenance services for landscaping companies included in our subscription model."
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://www.leafrushmarketing.com/landscaping-website-maintenance/#webpage",
    "name": "Landscaping Website Maintenance | Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com/landscaping-website-maintenance/",
    "description": "Landscaping website maintenance including portfolio updates, security, and backups. Included in our $200–$300/mo subscription.",
    "isPartOf": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    }
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://www.leafrushmarketing.com/landscaping-website-maintenance/#service",
    "name": "Landscaping Website Maintenance",
    "provider": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    },
    "description": "Ongoing website maintenance for landscaping companies including portfolio updates, security, and backups.",
    "areaServed": "United States",
    "serviceType": "Website Maintenance"
  };

  return (
    <>
      <SEO 
        title="Landscaping Website Maintenance | Leaf Rush"
        description="Landscaping website maintenance including portfolio updates, security, and backups. Included in our $200–$300/mo subscription."
        keywords="landscaping website maintenance, landscaper site maintenance, landscaping website updates, landscaping portfolio updates"
        canonical="https://www.leafrushmarketing.com/landscaping-website-maintenance/"
        ogUrl="https://www.leafrushmarketing.com/landscaping-website-maintenance/"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>

      <div className="flex flex-col">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="container px-4 md:px-6 relative z-10">
            <div className="mx-auto max-w-4xl text-center space-y-6">
              <Badge variant="secondary" className="mb-4">
                Portfolio Updates Included
              </Badge>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                Landscaping Website Maintenance With Portfolio Updates
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                Security, backups, performance optimization, and unlimited portfolio updates—all included in your monthly subscription.
              </p>
              
              {/* Quick Answer Box */}
              <Card className="bg-primary/5 border-primary/20 text-left max-w-2xl mx-auto">
                <CardContent className="pt-6">
                  <p className="font-semibold text-primary mb-2">Quick Answer:</p>
                  <p className="text-muted-foreground">
                    Landscaping website maintenance includes security, backups, and portfolio updates. Standalone costs $75–$200/month. Our subscription includes everything in $200–$300/month.
                  </p>
                </CardContent>
              </Card>

              {/* Not Sure Callout */}
              <div className="bg-accent/30 rounded-lg p-4 text-sm text-muted-foreground max-w-2xl mx-auto">
                <strong>Not sure if this is what you need?</strong> If you're looking for a complete website solution, visit our main{" "}
                <NavLink to="/landscaping-website-design/" className="text-primary underline hover:no-underline">
                  landscaping website design
                </NavLink>{" "}
                page to see everything included.
              </div>
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                What's Included in Landscaping Website Maintenance
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex gap-3">
                  <Image className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Portfolio Updates</p>
                    <p className="text-sm text-muted-foreground">
                      Add new projects, before/after galleries, and project details as you complete work.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Lock className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Security Updates</p>
                    <p className="text-sm text-muted-foreground">
                      Regular patches and monitoring to protect against vulnerabilities.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <RefreshCw className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Regular Backups</p>
                    <p className="text-sm text-muted-foreground">
                      Automated backups of your site and all your portfolio images.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Zap className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Image Optimization</p>
                    <p className="text-sm text-muted-foreground">
                      High-res project photos optimized for fast loading without quality loss.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Landscaping Sites Need More */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Why Landscaping Websites Need Specialized Maintenance
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Frequent Portfolio Updates</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      New projects need to be added regularly to show current work and maintain credibility.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Large Image Files</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      High-quality project photos require optimization to keep your site fast.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Seasonal Content</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Services and promotions change seasonally—your site needs to reflect that.
                    </p>
                  </CardContent>
                </Card>
              </div>
              <div className="text-center mt-8">
                <p className="text-muted-foreground">
                  See{" "}
                  <NavLink to="/landscaping-portfolio-website/" className="text-primary underline hover:no-underline">
                    landscaping portfolio website guide
                  </NavLink>{" "}
                  for portfolio best practices.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Comparison */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Maintenance Cost Comparison
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Option</th>
                      <th className="text-left py-3 px-4">Monthly Cost</th>
                      <th className="text-left py-3 px-4">Includes</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b bg-primary/5">
                      <td className="py-3 px-4 font-semibold">Our Subscription</td>
                      <td className="py-3 px-4">$200–$300 (all-in)</td>
                      <td className="py-3 px-4">Design + Hosting + Maintenance + Portfolio Updates</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4">Standalone Maintenance</td>
                      <td className="py-3 px-4">$75–$200</td>
                      <td className="py-3 px-4">Updates + Security (portfolio extra)</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4">DIY (Self-Managed)</td>
                      <td className="py-3 px-4">$0 (your time)</td>
                      <td className="py-3 px-4">You handle everything</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-center text-sm text-muted-foreground mt-4">
                See full pricing:{" "}
                <NavLink to="/websites/" className="text-primary underline hover:no-underline">
                  website subscription pricing
                </NavLink>{" "}
                |{" "}
                <NavLink to="/landscaping-website-cost/" className="text-primary underline hover:no-underline">
                  landscaping website cost breakdown
                </NavLink>
              </p>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Frequently Asked Questions About Landscaping Website Maintenance
              </h2>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* Related Guides */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Related Guides
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <NavLink to="/landscaping-website-design/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Landscaping Website Design</h3>
                  <p className="text-sm text-muted-foreground">Complete guide to professional landscaping websites.</p>
                </NavLink>
                <NavLink to="/landscaping-website-examples/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Landscaping Website Examples</h3>
                  <p className="text-sm text-muted-foreground">See examples of effective landscaping sites.</p>
                </NavLink>
                <NavLink to="/service-pages-that-rank/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Service Pages That Rank</h3>
                  <p className="text-sm text-muted-foreground">Build service pages that rank in Google.</p>
                </NavLink>
                <NavLink to="/resources/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">All Resources</h3>
                  <p className="text-sm text-muted-foreground">Browse all guides and resources.</p>
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section id="schedule-call" className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold">
                Ready for Hassle-Free Website Maintenance?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Book a free call to learn how our subscription includes complete website maintenance with portfolio updates.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <NavLink to="/websites/">
                  <Button size="lg" variant="outline">
                    View Website Subscription Pricing
                  </Button>
                </NavLink>
                <NavLink to="/contact/">
                  <Button size="lg">
                    <Phone className="mr-2 h-5 w-5" />
                    Contact Us
                  </Button>
                </NavLink>
              </div>
            </div>
            <div className="max-w-2xl mx-auto mt-10">
              <CalendlyEmbed />
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LandscapingWebsiteMaintenance;
